'use client';

import React from 'react';
import {
  Box,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';

import dynamic from 'next/dynamic';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import mixinSx from '@watershed/style/mixinSx';

import TextLink from './TextLink';
import { inlineCodeSx } from '@watershed/ui-core/components/InlineCode';

const ReactMarkdown = dynamic(() => import('react-markdown'));

const customReactMarkdownSx: SxProps<Theme> = {
  '&': {
    // This is the default, resetting to be defensive against e.g. Mui's Tooltip which
    // sets it to pre-line, which frequently looks weird.
    whiteSpace: 'normal',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: '8px',
    marginBottom: '8px',
  },

  '& details': {
    marginTop: 2,
    '& summary': {
      color: (theme) => theme.palette.text.primary,
    },
  },
  '& iframe': {
    width: '100%',
    maxWidth: '100%',
    border: 0,
    margin: (theme) => theme.spacing(1, 0, 2),
  },
  '& p + p': {
    marginTop: `8px`,
  },
  '& code': {
    ...inlineCodeSx,
    whiteSpace: 'pre-wrap',
  },
  '& td': {
    padding: `4px`,
  },
};

export default function CustomReactMarkdown({
  source,
  variant = 'body2' as TypographyProps['variant'],
  className,
  sx,
  onClickLink,
  linkOpensNewTab,
  ...reactMarkdownProps
}: {
  source: LocalizedString | null;
  variant?: TypographyProps['variant'];
  className?: TypographyProps['className'];
  sx?: SxProps<Theme>;
  onClickLink?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  linkOpensNewTab?: boolean;
} & Omit<React.ComponentProps<typeof ReactMarkdown>, 'children'>) {
  if (!source) {
    return <></>;
  }
  return (
    <Box sx={mixinSx(customReactMarkdownSx, sx)} className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          h1: ({ node, ...props }) => (
            <Typography
              variant="h1"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          h2: ({ node, ...props }) => (
            <Typography
              variant="h2"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          h3: ({ node, ...props }) => (
            <Typography
              variant="h4"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          h4: ({ node, ...props }) => (
            <Typography
              variant="h5"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          h5: ({ node, ...props }) => (
            <Typography
              variant="h5"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          h6: ({ node, ...props }) => (
            <Typography
              variant="h6"
              sx={{
                marginBottom: 2,
              }}
            >
              {props.children}
            </Typography>
          ),
          a: ({ node, ...props }) => (
            <TextLink
              href={props.href}
              onClick={onClickLink}
              newTab={linkOpensNewTab}
            >
              {props.children}
            </TextLink>
          ),
          p: ({ node, ...props }) => {
            return (
              <Typography
                sx={{ wordWrap: 'break-word' }}
                variant={variant}
                // Necessary to make explicit, because `body3` variant defaults to `span`
                component="p"
              >
                {props.children}
              </Typography>
            );
          },
          ul: ({ node, ...props }) => {
            return (
              <Box
                component="ul"
                sx={{ marginTop: 1, marginBottom: 1, paddingLeft: 2.5 }}
              >
                {props.children}
              </Box>
            );
          },
          ol: ({ node, ...props }) => {
            return (
              <Box
                component="ol"
                sx={{ marginTop: 1, marginBottom: 1, paddingLeft: 2.5 }}
              >
                {props.children}
              </Box>
            );
          },
          li: ({ node, ...props }) => {
            return (
              <Typography component="li" variant={variant}>
                {props.children}
              </Typography>
            );
          },
          td: ({ node, ...props }) => {
            return (
              <td>
                <Typography
                  variant={variant}
                  // Necessary to make explicit, because `body3` variant defaults to `span`
                  component="p"
                >
                  {props.children}
                </Typography>
              </td>
            );
          },
        }}
        {...reactMarkdownProps}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}
